import * as React from "react";
import { useMemo, useContext, useCallback, useEffect } from "react";
import styled from "styled-components";
import ReportingWidget from "report/common/ReportingWidget";
import { RenderCompletionObserver } from "components/visualizations";
import WidgetDetailsContext from "report/report-creation/WidgetDetailsContext";
import defaultWidgetTitle from "views/components/defaultTitle";
import Spinner from "components/common/Spinner";
import WidgetRenderingContext from "views/components/widgets/WidgetRenderingContext";
import { reportWidgetHeaderId } from "report/Constants";
import WidgetResultContext from "report/report-creation/WidgetResultContext";
import { Alert } from "components/bootstrap";
import RenderCompletionCallback from "views/components/widgets/RenderCompletionCallback";
import Heading from "common/components/widgetRenderers/Heading";
import WidgetTitle from "report/common/WidgetTitle";
import { getWidgetSizes } from "search/ExportWidgetAction/util/WidgetExportUtils";
const Visualization = styled.div`
  margin-top: 10px;
  margin-bottom: 30px;
`;
export const widgetTitle = (widget, completeWidget) => {
  var _a, _b;
  if ((_a = widget == null ? void 0 : widget.title) == null ? void 0 : _a.trim()) {
    return widget.title;
  }
  if (completeWidget) {
    return (_b = completeWidget == null ? void 0 : completeWidget.description) != null ? _b : defaultWidgetTitle(completeWidget);
  }
  return void 0;
};
const DeletedWidget = ({ widgetId, dashboardId }) => {
  const onRenderComplete = useContext(RenderCompletionCallback);
  useEffect(() => {
    onRenderComplete();
  }, [onRenderComplete]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    Heading,
    {
      title: /* @__PURE__ */ React.createElement(WidgetTitle, { isLoading: false, detailsAreAvailable: false, title: void 0 }),
      headerId: reportWidgetHeaderId(dashboardId, widgetId)
    }
  ), /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger", className: "has-bm" }, "This widget is not accessible. You are either missing the required permissions to view this widget or it has been removed from the related dashboard."));
};
const Widget = ({ hideDescription, hideQuery, widget, orientation, defaultWidth }) => {
  const { widgetId } = widget;
  const widgetSearchResult = useContext(WidgetResultContext);
  const { widgets: widgetDetails, isLoading } = useContext(WidgetDetailsContext);
  const completeWidget = widgetDetails[widgetId];
  const title = widgetTitle(widget, completeWidget);
  if (isLoading && !completeWidget) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  if (!isLoading && !completeWidget) {
    return /* @__PURE__ */ React.createElement(DeletedWidget, { widgetId, dashboardId: widget.dashboardId });
  }
  const { width, height } = getWidgetSizes(widget, orientation, defaultWidth);
  return /* @__PURE__ */ React.createElement(Visualization, null, /* @__PURE__ */ React.createElement(
    ReportingWidget,
    {
      widget: completeWidget,
      value: widgetSearchResult,
      showHandle: false,
      interactive: false,
      height,
      width,
      hideDescription,
      hideQuery,
      title,
      description: widget.description
    }
  ));
};
const widgetRendering = {
  limitHeight: false
};
const ReportWidgets = ({
  hideWidgetDescription,
  hideWidgetQuery,
  onWidgetRenderComplete,
  orientation,
  positions,
  widgets,
  widgetResultProvider: WidgetResultProvider,
  width: defaultWidth = void 0
}) => {
  const _orientation = orientation != null ? orientation : "portrait";
  const sortedWidgets = useMemo(
    () => widgets.sort((widgetA, widgetB) => {
      if (positions.length === 0) {
        return 0;
      }
      const positionA = positions.find((p) => p.dashboard_widget_id === widgetA.widgetId);
      const positionB = positions.find((p) => p.dashboard_widget_id === widgetB.widgetId);
      return positionA && positionB ? positionA.row - positionB.row : 0;
    }),
    [positions, widgets]
  );
  const handleRenderComplete = useCallback(
    (widgetId) => onWidgetRenderComplete(widgetId),
    [onWidgetRenderComplete]
  );
  return /* @__PURE__ */ React.createElement(WidgetRenderingContext.Provider, { value: widgetRendering }, sortedWidgets.map((widget) => /* @__PURE__ */ React.createElement(WidgetResultProvider, { key: widget.widgetId, widgetId: widget.widgetId, dashboardId: widget.dashboardId }, /* @__PURE__ */ React.createElement(RenderCompletionObserver, { onRenderComplete: handleRenderComplete(widget.widgetId) }, /* @__PURE__ */ React.createElement(
    Widget,
    {
      widget,
      defaultWidth,
      key: widget.widgetId,
      hideDescription: hideWidgetDescription,
      hideQuery: hideWidgetQuery,
      orientation: _orientation
    }
  )))));
};
export default React.memo(ReportWidgets);
